import { safeFetch, resetCircuitBreaker } from '../utils/requestUtils';
import { getSession } from '../api/supabase';

/**
 * Enhanced API service with circuit breaker and error handling
 */
class APIService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get authorization headers from Supabase session
   */
  async getAuthHeaders() {
    try {
      const session = await getSession();
      const token = session?.access_token;
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {};
    }
  }

  /**
   * Check if error is a billing-related 403 (not auth)
   */
  isBillingError(error) {
    if (error.status !== 403) return false;

    const errorData = error.data || error;
    const errorCode = errorData.error || errorData.code || errorData.message || '';

    // Check for billing-related indicators
    return (
      errorCode.toLowerCase().includes('limit') ||
      errorCode.toLowerCase().includes('upgrade') ||
      errorCode.toLowerCase().includes('feature') ||
      errorData.limit_type ||
      errorData.upgrade_url
    );
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(error) {
    if (error.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    throw error;
  }

  /**
   * Enhanced fetch wrapper
   */
  async fetch(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const authHeaders = await this.getAuthHeaders();
    const headers = {
      ...this.defaultHeaders,
      ...authHeaders,
      ...options.headers
    };

    try {
      return await safeFetch(url, {
        ...options,
        headers
      });
    } catch (error) {
      // Don't redirect for billing-related 403s - let components handle them
      if (error.status === 403 && this.isBillingError(error)) {
        // Enhance the error with billing info for components to use
        error.isBillingError = true;
        throw error;
      }

      // Handle authentication errors (401 and non-billing 403s)
      if (error.status === 401 || error.status === 403) {
        return this.handleAuthError(error);
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}, options = {}) {
    return this.fetch(endpoint, {
      method: 'GET',
      params,
      ...options
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = {}, options = {}) {
    return this.fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = {}, options = {}) {
    return this.fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.fetch(endpoint, {
      method: 'DELETE',
      ...options
    });
  }

  /**
   * Manual recovery for circuit breaker
   */
  resetEndpointCircuitBreaker(endpoint) {
    const url = `${this.baseURL}${endpoint}`;
    resetCircuitBreaker(url);
  }
}

// Create singleton instance
const apiService = new APIService();

export default apiService;

// Export specific methods for backwards compatibility
export const API = {
  get: (endpoint, params, options) => apiService.get(endpoint, params, options),
  post: (endpoint, data, options) => apiService.post(endpoint, data, options),
  put: (endpoint, data, options) => apiService.put(endpoint, data, options),
  delete: (endpoint, options) => apiService.delete(endpoint, options),
  resetCircuitBreaker: (endpoint) => apiService.resetEndpointCircuitBreaker(endpoint)
};